import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/Dashboard/DashboardLayout';
import PageHeader from '../components/ui/PageHeader';
import SegmentedTabs from '../components/ui/SegmentedTabs';
import { bookmarksAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useQuery, STALE } from '../hooks/useQuery';
import { setQueryData, makeKey } from '../lib/queryCache';
import { ListRowsSkeleton, ErrorState } from '../components/ui/Skeleton';
import {
  Bookmark,
  Trash2,
  BookOpen,
  Languages,
  Sparkles,
  ExternalLink,
} from 'lucide-react';

const TYPE_META = {
  lesson: {
    label: 'Mësim',
    icon: BookOpen,
    color: 'bg-blue-50 text-blue-700 border-blue-200',
    link: '/exam/b2',
  },
  grammar_topic: {
    label: 'Temë Gramatike',
    icon: Sparkles,
    color: 'bg-amber-50 text-amber-800 border-amber-200',
    link: '/grammar',
  },
  word: {
    label: 'Fjalë',
    icon: Languages,
    color: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    link: '/vocabulary',
  },
};

const TABS = ['Të gjitha', 'Mësim', 'Temë Gramatike', 'Fjalë'];
const TAB_TYPES = {
  'Të gjitha': null,
  Mësim: 'lesson',
  'Temë Gramatike': 'grammar_topic',
  Fjalë: 'word',
};

const BookmarksPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('Të gjitha');
  const [removing, setRemoving] = useState(null);
  const [removeError, setRemoveError] = useState(null);

  const {
    data: fetchedBookmarks,
    isLoading,
    error: queryError,
    refetch,
  } = useQuery(
    'bookmarks',
    activeTab,
    async () => {
      const res = await bookmarksAPI.list(TAB_TYPES[activeTab]);
      return res.data.bookmarks || [];
    },
    { staleTime: STALE.medium }
  );

  const [localBookmarks, setLocalBookmarks] = useState(null);

  useEffect(() => {
    if (fetchedBookmarks) {
      setLocalBookmarks(fetchedBookmarks);
    }
  }, [fetchedBookmarks]);

  const bookmarks = localBookmarks || fetchedBookmarks || [];

  const handleRemove = async (id) => {
    setRemoving(id);
    setRemoveError(null);
    const previous = bookmarks;
    const optimistic = bookmarks.filter((b) => b.id !== id);
    setLocalBookmarks(optimistic);
    if (user?.id) {
      const key = makeKey(user.id, 'bookmarks', activeTab);
      setQueryData(key, optimistic);
    }
    try {
      await bookmarksAPI.remove(id);
    } catch (e) {
      console.error(e);
      setRemoveError('Nuk u fshi dot elementi i ruajtur.');
      setLocalBookmarks(previous);
      if (user?.id) {
        const key = makeKey(user.id, 'bookmarks', activeTab);
        setQueryData(key, previous);
      }
    } finally {
      setRemoving(null);
    }
  };

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-[1440px] space-y-6 px-1 pb-10 font-sans sm:px-3">
        {/* Unified Header */}
        <PageHeader
          icon={Bookmark}
          badge="Koleksioni yt"
          title="Të Ruajturat"
          description="Këtu gjenden mësimet, temat gramatikore dhe fjalët që ke ruajtur për t'i parë më vonë."
        >
          {/* Segmented Filter Pills */}
          <SegmentedTabs
            ariaLabel="Filtro të ruajturat"
            tabs={TABS.map((tab) => ({ id: tab, label: tab }))}
            value={activeTab}
            onChange={setActiveTab}
          />
        </PageHeader>

        {removeError && <ErrorState message={removeError} />}

        {queryError && !bookmarks.length && (
          <ErrorState message="Nuk mund të ngarkoheshin të ruajturat." onRetry={refetch} />
        )}

        {/* List of Bookmarked Items */}
        {isLoading ? (
          <ListRowsSkeleton rows={4} height="h-16" />
        ) : bookmarks.length === 0 ? (
          <div className="overflow-hidden rounded-2xl border border-dashed border-zinc-200 bg-white p-12 text-center shadow-2xs">
            <Bookmark className="mx-auto mb-3 h-10 w-10 text-zinc-300" />
            <h2 className="text-sm font-bold text-zinc-700">Asnjë element i ruajtur</h2>
            <p className="mt-1 text-xs text-zinc-400">
              Kliko ikonën e faqeruajtësit në leksione ose fjalor për t’i gjetur shpejt këtu.
            </p>
          </div>
        ) : (
          <div className="space-y-2.5">
            {bookmarks.map((bm) => {
              const meta = TYPE_META[bm.content_type] || TYPE_META.lesson;
              const Icon = meta.icon;
              return (
                <div
                  key={bm.id}
                  className="group flex items-center justify-between gap-3 rounded-xl border border-zinc-200/80 bg-white p-3.5 shadow-2xs transition hover:border-zinc-300 hover:bg-zinc-50/50"
                >
                  <div className="flex items-center gap-3.5 min-w-0 flex-1">
                    <div
                      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border ${meta.color}`}
                    >
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-bold text-zinc-900 group-hover:text-[#DD0000] transition">
                        {bm.content_title || bm.content_id}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5 text-xs text-zinc-400">
                        <span className="font-semibold text-zinc-600">{meta.label}</span>
                        <span>•</span>
                        <span>
                          {new Date(bm.created_at).toLocaleDateString('sq-AL', {
                            day: 'numeric',
                            month: 'short',
                          })}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5 shrink-0">
                    {meta.link && (
                      <Link
                        to={meta.link}
                        className="flex h-8 w-8 items-center justify-center rounded-lg text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700 transition"
                        title="Hap këtë element"
                      >
                        <ExternalLink size={15} />
                      </Link>
                    )}
                    <button
                      type="button"
                      onClick={() => handleRemove(bm.id)}
                      disabled={removing === bm.id}
                      className="flex h-8 w-8 items-center justify-center rounded-lg text-zinc-400 hover:bg-red-50 hover:text-red-600 transition disabled:opacity-40"
                      title="Hiq nga të ruajturat"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default BookmarksPage;
